<script setup lang="ts">
    import type { BikesCollectionData } from '~/@types/cms';
    import type { IWidgetProps } from '~/composables/widgets';
    import type { CollectionQuery, ProductPreview } from '~/graphql/generated';

    /**
     * Products Widget Container
     * loads a collection via the api and renders a set of cards for each product
     * while loading the collection, a skeleton should be rendered
     * we only fetch the first collection in the array
     */
    const props = defineProps<IWidgetProps<BikesCollectionData>>();
    const { data } = useWidget(props);
    const { country, language } = useLocales();
    const _id = `${country.value}:${language.value}:collection:${props.data.handle}`;

    const { previewEnabed, queryParams } = usePreview();
    const { resolveUrlLink } = useURL();

    const key = `${_id}-${previewEnabed.value}`;

    const products = computed<Partial<ProductPreview>[]>(() => {
        return (bikes.value?.data?.collection?.products?.items ?? []) as Partial<ProductPreview>[];
    });

    const title = computed(() => {
        return props.data?.intro?.title;
    });
    const description = computed(() => {
        return props.data?.intro?.subtitle;
    });
    const bgColor = computed(() => {
        return props.data?.style?.background_color;
    });
    const bg = computed(() => {
        return props.data?.style?.backgroundimage;
    });
    const linkText = computed(() => {
        return props.data?.link?.label ?? '';
    });
    const linkUrl = computed(() => {
        const { url } = resolveUrlLink(props.data?.link);
        return url;
    });

    const { data: bikes } = await useAsyncData(
        key,
        () => {
            // TODO: type response as  CollectionQuery
            return new Promise<ExtendedApolloQueryResult<CollectionQuery>>((resolve, reject) => {
                const { data: cachedData } = useNuxtData(key);
                const cache = cachedData ? unref(cachedData) : undefined;
                if (cache) {
                    resolve(cache);
                } else {
                    $fetch('/api/collection', {
                        query: {
                            id: _id,
                            ...queryParams.value,
                        },
                    })
                        .then((result) => {
                            resolve(result as ExtendedApolloQueryResult<CollectionQuery>);
                        })
                        .catch((err) => {
                            reject(err);
                        });
                }
            });
        },
        {
            immediate: true,
            server: true,
        },
    );
</script>
<template>
    <org-carousel
        v-if="products?.length"
        :products="products"
        :link="linkUrl"
        :link-text="linkText"
        :title="title"
        :color-filter="data.filter"
        :background-color="bgColor"
        :background-image="bg"
        :description="description">
    </org-carousel>
</template>
